import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'
import { Temporal } from '@js-temporal/polyfill'

import type { ResultOf } from '~publish/graphql'

import { hexToBannerColor } from '~publish/components/TemplateManager/schemas'
import { useOrganizationId } from '~publish/contexts/AccountContext'

import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_TYPE_LABELS,
  GOAL_LABELS,
  narrowTags,
  narrowUserTypes,
  STRUCTURE_LABELS,
  TYPE_LABELS,
} from '../labels'
import type { Template, TemplateCampaign } from '../types'
import {
  PostTemplateCampaignsLibraryQuery,
  PostTemplatesQuery,
} from '../graphql/postTemplates'

const COLORS = [
  '255, 203, 49',
  '218, 255, 53',
  '99, 117, 255',
  '248, 149, 151',
  '87, 152, 255',
  '59, 252, 255',
]

type ApiTemplate = NonNullable<
  ResultOf<typeof PostTemplatesQuery>['postTemplates']
>['edges'][number]['node']

type ApiCampaign = NonNullable<
  ResultOf<typeof PostTemplateCampaignsLibraryQuery>['postTemplateCampaigns']
>['edges'][number]['node']

function mapTemplate(
  node: ApiTemplate,
  index: number,
  campaignsByTemplateId: Map<string, string[]>,
): Template {
  return {
    id: node.id,
    title: node.title,
    body: node.body,
    description: node.description ?? '',
    emoji: node.emoji ?? '📝',
    type: TYPE_LABELS[node.type] ?? 'Tip',
    goal: GOAL_LABELS[node.goal] ?? 'Engagement',
    structure: STRUCTURE_LABELS[node.structure] ?? 'Single Text',
    userTypes: narrowUserTypes(node.userTypes),
    tags: narrowTags(node.tags),
    featured: node.featured ?? false,
    createdAt: node.createdAt,
    color: COLORS[index % COLORS.length],
    linkedCampaigns: campaignsByTemplateId.get(node.id) ?? [],
  }
}

function mapCampaign(node: ApiCampaign): TemplateCampaign {
  const bannerColorName = hexToBannerColor(node.bannerColor)
  return {
    id: node.id,
    name: node.name,
    title: node.title ?? undefined,
    heading: node.heading ?? '',
    description: node.description ?? undefined,
    status: CAMPAIGN_STATUS_LABELS[node.status] ?? 'Draft',
    campaignType: CAMPAIGN_TYPE_LABELS[node.campaignType] ?? 'Collection',
    illustration: node.illustration ?? undefined,
    bannerColor: bannerColorName !== 'none' ? bannerColorName : undefined,
    startDate: Temporal.ZonedDateTime.from(
      `${node.startDate}[America/New_York]`,
    ),
    endDate: Temporal.ZonedDateTime.from(`${node.endDate}[America/New_York]`),
    toggleTip: node.toggleTip ?? undefined,
    showTemplatesAfterEndDate: node.showTemplatesAfterEndDate ?? false,
    supportsBanner: node.supportsBanner ?? undefined,
    templateIds: (node.templates ?? []).map((t) => t.id),
  }
}

export function usePostTemplates(): {
  loading: boolean
  templates: Template[]
  campaigns: TemplateCampaign[]
} {
  const organizationId = useOrganizationId()

  const {
    data: templatesData,
    dataState: templatesDataState,
    loading: templatesLoading,
  } = useQuery(PostTemplatesQuery, {
    // Only public templates for now — internal/private are not yet
    // surfaced in the UI. Server-side, the visibility filter is a
    // narrowing selector within the actor-scoped union, never a way
    // to escape it.
    variables: {
      input: {
        organizationId: organizationId ?? '',
        filter: { visibility: 'public' },
      },
      first: 500,
    },
    skip: !organizationId,
  })

  const {
    data: campaignsData,
    dataState: campaignsDataState,
    loading: campaignsLoading,
  } = useQuery(PostTemplateCampaignsLibraryQuery, {
    variables: { input: { filter: { status: 'active' } }, first: 100 },
  })

  const completeTemplatesData =
    templatesDataState === 'complete' ? templatesData : undefined
  const completeCampaignsData =
    campaignsDataState === 'complete' ? campaignsData : undefined

  const campaigns = useMemo(() => {
    if (!completeCampaignsData?.postTemplateCampaigns?.edges) return []

    return completeCampaignsData.postTemplateCampaigns.edges.map((edge) =>
      mapCampaign(edge.node),
    )
  }, [completeCampaignsData])

  const templates = useMemo(() => {
    if (!completeTemplatesData?.postTemplates?.edges) return []

    const campaignsByTemplateId = new Map<string, string[]>()
    for (const campaign of campaigns) {
      for (const templateId of campaign.templateIds) {
        const existing = campaignsByTemplateId.get(templateId) ?? []
        existing.push(campaign.id)
        campaignsByTemplateId.set(templateId, existing)
      }
    }

    return completeTemplatesData.postTemplates.edges.map((edge, index) =>
      mapTemplate(edge.node, index, campaignsByTemplateId),
    )
  }, [completeTemplatesData, campaigns])

  return {
    loading: templatesLoading || campaignsLoading,
    templates,
    campaigns,
  }
}
